import React, { useEffect, useState } from "react";
import Layout from "../../../../Components/Common/Layout.js";
import DashLayout from "../../../../Components/Admin/AdminDashLayout.js";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import { fetchDayBook } from "../../../../Store/Slices/Finance/dayBook/dayBook.thunk.js";
import { useTranslation } from "react-i18next";
import useNavHeading from "../../../../Hooks/CommonHooks/useNavHeading .js";
import ProtectedSection from "../../../../Routes/ProtectedRoutes/ProtectedSection.js";
import { PERMISSIONS } from "../../../../config/permission.js";
import TableShimmer from "../../../../Components/Common/TableShimmer.jsx";
// import TotalEarningsGraph from "./TotalEarningsGraph.jsx";
import dayjs from "dayjs";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiArrowUpRight,
  FiArrowDownRight,
  FiDownload,
  FiChevronRight,
} from "react-icons/fi";
import { MdOutlineCreditCard, MdOutlineAccountBalance } from "react-icons/md";
import { HiOutlineDocumentText } from "react-icons/hi";
import {
  IoSparklesOutline,
  IoAlertCircleOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import { analyzeFinanceData } from "../../../../services/geminiService.js";
import { BsGraphDownArrow } from "react-icons/bs";
import { LuCalendarDays } from "react-icons/lu";
import TotalEarningsGraph from "../../Dashboard/Graphs/TotalEarningsGraph.js";

const FinanceOverview = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("prtFinance");
  const role = useSelector((store) => store.common.auth.role);
  const schoolCurrency = useSelector(
    (store) => store.common.user.userDetails?.currency,
  );

  useNavHeading(role, "Finance Overview");

  const [dayBookData, setDayBookData] = useState({});
  const [loading, setLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchDayBook({ page: 1, limit: 10 }))
      .then((action) => {
        setDayBookData(action?.payload);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [dispatch]);

  const totalRevenue = dayBookData?.totalIncome ?? 0;
  const totalExpenses = dayBookData?.totalExpense ?? 0;
  const netProfit = totalRevenue - totalExpenses;

  // Generate AI insights once real finance data is available
  useEffect(() => {
    if (!totalRevenue && !totalExpenses) return;
    let cancelled = false;
    const generate = async () => {
      setAiLoading(true);
      try {
        const results = await analyzeFinanceData({
          totalRevenue,
          totalExpenses,
          netProfit,
          recentTransactions: (dayBookData?.data || []).slice(0, 10).map((tx) => ({
            type: tx.transactionType,
            amount: tx.amount,
            category: tx.category,
          })),
        });
        if (!cancelled) setAiInsights(Array.isArray(results) ? results : []);
      } catch {
        /* silently fail — non-critical */
      } finally {
        if (!cancelled) setAiLoading(false);
      }
    };
    generate();
    return () => { cancelled = true; };
  }, [totalRevenue, totalExpenses]);

  const statCards = [
    {
      label: "Total Revenue",
      value: totalRevenue,
      // change: "+12.5%",
      changeColor: "text-green-500",
      icon: (
        <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
          <span className="text-green-500 text-lg font-bold">$</span>
        </div>
      ),
      arrow: <FiArrowUpRight className="w-3.5 h-3.5" />,
    },
    {
      label: "Total Expenses",
      value: totalExpenses,
      // change: "-5.2%",
      changeColor: "text-red-500",
      icon: (
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
          <FiTrendingDown className="w-5 h-5 text-red-500" />
        </div>
      ),
      arrow: <FiArrowDownRight className="w-3.5 h-3.5" />,
    },
    {
      label: "Net Profit",
      value: netProfit,
      // change: "+28.4%",
      changeColor: "text-green-500",
      icon: (
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
          <FiTrendingUp className="w-5 h-5 text-blue-500" />
        </div>
      ),
      arrow: <FiArrowUpRight className="w-3.5 h-3.5" />,
    },
  ];

  const txColumns = [
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Entity / Student
        </span>
      ),
      dataIndex: "linkedTo",
      key: "linkedTo",
      render: (text, record) => (
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-800">
            {text || "—"}
          </span>
          <span className="text-xs text-gray-400 capitalize">
            {record.transactionType}
          </span>
        </div>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Category
        </span>
      ),
      dataIndex: "category",
      key: "category",
      render: (text) => (
        <span className="inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold capitalize tracking-wider bg-purple-50 text-purple-600 border border-purple-100">
          {text || "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Date
        </span>
      ),
      dataIndex: "date",
      key: "date",
      render: (val) => (
        <span className="text-sm text-gray-600">
          {val ? dayjs(val).format("DD MMM YYYY") : "—"}
        </span>
      ),
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Status
        </span>
      ),
      dataIndex: "transactionType",
      key: "status",
      render: (val) => {
        const isRevenue = val === "revenue";
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold capitalize tracking-wider border ${isRevenue
              ? "bg-green-50 text-green-600 border-green-200"
              : "bg-red-50 text-red-500 border-red-100"
              }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isRevenue ? "bg-green-500" : "bg-red-400"
                }`}
            />
            {val}
          </span>
        );
      },
    },
    {
      title: (
        <span className="text-xs font-semibold uppercase tracking-widest text-gray-400">
          Amount
        </span>
      ),
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (val, record) => {
        const isRevenue = record.transactionType === "revenue";
        return (
          <span
            className={`text-sm font-bold ${isRevenue ? "text-green-600" : "text-red-500"
              }`}
          >
            {isRevenue ? "+" : "-"}
            {val?.toFixed(2)} {schoolCurrency}
          </span>
        );
      },
    },
  ];

  const quickActions = [
    {
      label: "Collect Fees",
      icon: <MdOutlineCreditCard className="w-7 h-7 text-purple-500" />,
      bg: "bg-purple-50",
      path: "/finance/studentfees",
    },
    {
      label: "Record Expense",
      icon: <BsGraphDownArrow className="w-6 h-6 text-rose-500" />,
      bg: "bg-rose-50",
      path: "/finance/operational-expenses",
    },
    {
      label: "Generate Invoice",
      icon: <HiOutlineDocumentText className="w-7 h-7 text-teal-500" />,
      bg: "bg-teal-50",
      path: "/finance/entity/revenue",
    },
    {
      label: "Reconcile Bank",
      icon: <MdOutlineAccountBalance className="w-7 h-7 text-blue-500" />,
      bg: "bg-blue-50",
      path: "/finance/bank-reconciliation",
    },
  ];


  return (
    <Layout title="Finance Overview | Office Flow">
      <DashLayout>
        <ProtectedSection
          requiredPermission={PERMISSIONS.VIEW_FINANCIAL_OVERVIEW}
          title="Finance Overview"
        >
          <div className="min-h-screen bg-gray-50 p-4 flex flex-col gap-5">
            <div className="flex items-center justify-between gap-4 flex-wrap bg-white rounded-xl shadow-sm p-4 ">
              <div>
                <h1 className="page-heading w-96">
                  {t("Finance Overview")}
                </h1>
                <p className="subheading">
                  {t("Monitor your school's financial health and AI-driven insights.")}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
                {/* <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition">
                  <LuCalendarDays className="w-4 h-4" />
                  {t("Last 30 Days")}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition">
                  <FiDownload className="w-4 h-4" />
                  {t("Export")}
                </button> */}
                <button
                  className="flex items-center gap-2 px-5 py-2 btn-gradient"
                  onClick={() => window.print()}
                >
                  <IoSparklesOutline className="w-4 h-4" />
                  {t("Print Report")}
                </button>
              </div>
            </div>
            <div id="print-report">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                {statCards.map((card) => (
                  <div
                    key={card.label}
                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5"
                  >
                    <div className="flex items-center justify-between mb-5">
                      {card.icon}
                      <span
                        className={`flex items-center gap-0.5 text-xs font-semibold ${card.changeColor}`}
                      >
                        {card.arrow}
                        {card.change}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-1">{card.label}</p>
                    <p className="text-2xl font-extrabold text-gray-900">
                      {schoolCurrency} {card.value?.toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <TotalEarningsGraph isOverview={true} />
                </div>

                <div className="no-print bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <IoSparklesOutline className="w-5 h-5 text-purple-500" />
                      <h3 className="text-base font-bold text-gray-900">
                        {t("AI Insights")}
                      </h3>
                    </div>
                    {aiInsights.length > 0 && (
                      <button
                        onClick={() => navigate("/intelligence/ai-center", { state: { openChat: true, prefilledQuery: "Analyze my finance data" } })}
                        className="text-xs text-purple-600 font-semibold hover:text-purple-800 flex items-center gap-1"
                      >
                        {t("View All")} <FiChevronRight className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {aiLoading ? (
                    <div className="flex flex-col gap-3 mt-1">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse flex gap-2 items-start">
                          <div className="w-4 h-4 rounded-full bg-gray-200 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-3 bg-gray-200 rounded w-3/4" />
                            <div className="h-2.5 bg-gray-100 rounded w-full" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : aiInsights.length > 0 ? (
                    <div className="flex flex-col gap-2.5 overflow-y-auto max-h-72">
                      {aiInsights.map((insight, i) => {
                        const typeMap = {
                          DANGER: { dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50", badge: "bg-red-100 text-red-700" },
                          WARNING: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", badge: "bg-amber-100 text-amber-700" },
                          INFO: { dot: "bg-blue-500", text: "text-blue-700", bg: "bg-blue-50", badge: "bg-blue-100 text-blue-700" },
                          SUCCESS: { dot: "bg-green-500", text: "text-green-700", bg: "bg-green-50", badge: "bg-green-100 text-green-700" },
                        };
                        const cfg = typeMap[insight.type] || typeMap.INFO;
                        return (
                          <div key={i} className={`rounded-xl p-3 ${cfg.bg} flex gap-2.5 items-start`}>
                            <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${cfg.dot}`} />
                            <div className="flex-1 min-w-0">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full mr-1 ${cfg.badge}`}>
                                {insight.type}
                              </span>
                              <span className={`text-xs font-medium ${cfg.text}`}>{insight.text}</span>
                              {insight.reasoning && (
                                <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-2">{insight.reasoning}</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 gap-2 text-gray-400">
                      <IoSparklesOutline className="w-8 h-8 opacity-30" />
                      <p className="text-xs">{t("No insights yet")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>


            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  onClick={() => navigate(action.path)}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm py-6 px-4 flex flex-col items-center gap-3 hover:shadow-md hover:border-purple-100 transition-all duration-200 group"
                >
                  <div
                    className={`w-12 h-12 rounded-xl ${action.bg} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}
                  >
                    {action.icon}
                  </div>
                  <span className="text-sm font-semibold text-gray-700">
                    {action.label}
                  </span>
                </button>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    {t("Recent Transactions")}
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {t("Your latest financial activities")}
                  </p>
                </div>
                <button
                  onClick={() => navigate("/finance/day-book")}
                  className="flex items-center gap-1 text-sm font-bold text-purple-600 hover:text-purple-800 uppercase tracking-wide transition"
                >
                  {t("View All")}
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Table */}
              {loading ? (
                <TableShimmer columns={5} rows={5} />
              ) : (
                <Table
                  dataSource={dayBookData?.data?.slice(0, 10) || []}
                  columns={txColumns}
                  rowKey={(_, idx) => idx}
                  rowClassName="hover:bg-gray-50 transition-colors"
                  pagination={false}
                  components={{
                    body: {
                      row: (props) => (
                        <tr {...props} style={{ height: "64px" }} />
                      ),
                    },
                  }}
                  locale={{
                    emptyText: (
                      <div className="flex flex-col items-center py-16 gap-3 text-gray-400">
                        <IoInformationCircleOutline className="w-12 h-12 text-gray-300" />
                        <p className="text-sm font-semibold text-gray-500">
                          {t("No transactions found")}
                        </p>
                        <p className="text-xs text-gray-400">
                          {t("Start by recording your first transaction")}
                        </p>
                      </div>
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </ProtectedSection>
      </DashLayout>
    </Layout>
  );
};

export default FinanceOverview;
